/**
 * Rosie -- NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const user = JSON.parse(localStorage.getItem('nemt_user') || '{}');

const STATUS_COLORS = {
  open: { bg: '#FFF3E0', color: '#E65100' },
  in_progress: { bg: '#E3F2FD', color: '#1565C0' },
  resolved: { bg: '#E8F5E9', color: '#2E7D32' },
  dismissed: { bg: '#f5f5f3', color: '#888' },
};

const CATEGORIES = [
  { value: '', label: '-- Select --' },
  { value: 'bug', label: 'Bug / Something broken' },
  { value: 'duplicate', label: 'Duplicate trip' },
  { value: 'billing', label: 'Billing issue' },
  { value: 'scheduling', label: 'Scheduling issue' },
  { value: 'feature', label: 'Feature request / Idea' },
  { value: 'data', label: 'Data correction needed' },
  { value: 'other', label: 'Other' },
];

export default function Issues() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState('');
  const [details, setDetails] = useState('');
  const [category, setCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const fetchIssues = async () => {
    try {
      const res = await api.get('/api/admin-chat/issues');
      setIssues(res.data.issues || []);
    } catch (e) {
      console.error('Failed to load issues');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchIssues(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!summary.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/api/admin-chat/issues', {
        summary: summary.trim(),
        details: details.trim() || null,
        category: category || null,
        reported_by: user.name || user.email || 'unknown',
        reporter_role: user.role || 'staff',
      });
      setSummary('');
      setDetails('');
      setCategory('');
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
      fetchIssues();
    } catch (e) {
      alert('Failed to submit issue. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/api/admin-chat/issues/${id}`, { status });
      fetchIssues();
    } catch (e) {
      alert('Failed to update issue.');
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 20 }}>Issues & Feedback</h1>

      {/* Submit form */}
      <form onSubmit={handleSubmit} style={{ background: '#fff', border: '1px solid #e0e0db', borderRadius: 10, padding: 20, marginBottom: 28 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Report an issue or share feedback</div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 3 }}>Category</label>
          <select value={category} onChange={e => setCategory(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14 }}>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 3 }}>Summary *</label>
          <input
            value={summary}
            onChange={e => setSummary(e.target.value)}
            placeholder="Brief description of the issue"
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }}
            required
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 3 }}>Details (optional)</label>
          <textarea
            value={details}
            onChange={e => setDetails(e.target.value)}
            placeholder="Any additional context -- trip numbers, what you expected, etc."
            rows={3}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="submit"
            disabled={submitting || !summary.trim()}
            style={{ padding: '8px 20px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer', opacity: submitting ? 0.6 : 1 }}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
          {submitted && <span style={{ fontSize: 13, color: '#2E7D32' }}>Submitted! Thanks for the feedback.</span>}
        </div>
      </form>

      {/* Issues list */}
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Open Issues ({issues.filter(i => i.status === 'open' || i.status === 'in_progress').length})</div>

      {loading ? (
        <div style={{ color: '#999', fontSize: 13, padding: 20 }}>Loading...</div>
      ) : issues.length === 0 ? (
        <div style={{ color: '#999', fontSize: 13, padding: 20 }}>No issues yet.</div>
      ) : (
        issues.map(issue => {
          const sc = STATUS_COLORS[issue.status] || STATUS_COLORS.open;
          return (
            <div key={issue.id} style={{ background: '#fff', border: '1px solid #e0e0db', borderRadius: 8, padding: '14px 16px', marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{issue.summary}</div>
                  {issue.details && <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{issue.details}</div>}
                  <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>
                    {issue.reported_by} &middot; {new Date(issue.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' })}
                    {issue.category && <> &middot; {issue.category}</>}
                    {issue.source && <> &middot; via {issue.source}</>}
                  </div>
                  {issue.resolution && <div style={{ fontSize: 12, color: '#444', marginTop: 4, fontStyle: 'italic' }}>Resolution: {issue.resolution}</div>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: sc.bg, color: sc.color, fontWeight: 600 }}>{issue.status}</span>
                  {issue.status === 'open' && (
                    <button onClick={() => updateStatus(issue.id, 'resolved')} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', color: '#2E7D32' }}>Resolve</button>
                  )}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
